import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import editReserve from "@/features/reserves/api/editReserve";
import getReserves from "@/features/reserves/api/getReserves";
import AddReserveButton from "@/features/reserves/components/addReserve/AddReserveButton";
import ReserveTable from "@/features/reserves/components/table/ReserveTable";
import { ReserveBaseModel } from "@/features/reserves/types/reserveModels";
import { OverviewItemsModel } from "@/shared/components/baselineOverview/model/baselineOverviewModels";
import useProjectWBSContext from "@/shared/context/projectWBS/useProjectWBSContext";

type ReserveTableProps = {
  reservesData: ReserveBaseModel[];
  overviewItems: OverviewItemsModel[];
};

export default function ReserveBaselineView({
  reservesData,
  overviewItems,
}: ReserveTableProps) {
  const [reserves, setReserves] = useState<ReserveBaseModel[]>([]);
  const { outcome: wbsEntryId } = useParams();

  const { refetchProjectWBS, getWBSEntryById } = useProjectWBSContext();
  const WBSEntry = getWBSEntryById(wbsEntryId);

  useEffect(() => {
    setReserves(reservesData);
  }, [reservesData]);

  const handleSaveCell = async (
    reserveToUpdate: ReserveBaseModel,
    name: string,
    value: number | string
  ) => {
    const updatedReserve = {
      ...reserveToUpdate,
      [name]: value,
    };
    try {
      const ReserveBaseline = await editReserve(updatedReserve);
      setReserves((prevReserves) =>
        prevReserves.map((reserve) =>
          reserve.id === ReserveBaseline.id ? ReserveBaseline : reserve
        )
      );
      refetchProjectWBS();
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to save cell: ${error.message}`);
      } else {
        throw new Error("An unknown error occurred while saving cell");
      }
    }
  };

  const handleRefreshReserve = async () => {
    try {
      if (!WBSEntry?.projectId) {
        throw new Error("Project ID is null");
      }
      const updatedReserves = await getReserves(WBSEntry.projectId);
      refetchProjectWBS();
      setReserves(updatedReserves);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to refresh reserves: ${error.message}`);
      } else {
        throw new Error("An unknown error occurred while refreshing reserves");
      }
    }
  };

  return (
    <>
      <AddReserveButton refetchReserve={() => void handleRefreshReserve()} />
      <ReserveTable
        reserves={reserves}
        handleSaveCell={(reserveToUpdate, columnId, newValue) =>
          void handleSaveCell(reserveToUpdate, columnId, newValue)
        }
        refetchReserve={() => void handleRefreshReserve()}
        overviewItems={overviewItems}
      />
    </>
  );
}
